<template>
  <!--
    Two sidebars in one. From md up, on a screen tall enough, it is a column that is always shown
    (`sidebar--column`, see _sidebar.scss) and the header's button collapses it to icons. Below md,
    on a phone on its side and on a touch screen narrower than lg, a tablet upright, it is
    b-sidebar's own off-canvas: the same button opens it over the page, and the backdrop, the next
    navigation or the window growing into the column closes it.
  -->
  <b-sidebar
    id="sidebar"
    v-model="isDrawerOpen"
    :sidebar-class="sidebarClass"
    aria-label="Navigation"
    no-header
    backdrop
    bg-variant="-"
    text-variant="-"
  >
    <b-nav vertical>
      <template v-for="(menu, $idx) in visibleMenuOptions">
        <SidebarNavItem
          v-if="!menu.children && (!menu.condition || menu.condition())"
          :key="$idx"
          :route="!!menu.click ? null : menu.route"
          :menu-text="menu.menuText"
          :menu-icon="menu.menuIcon"
          :href="menu.href"
          :exact="menu.exact"
          :badge="menu.badge"
          :target="menu.target"
          :item-class="menu.itemClass"
          @click=";(menu.click && menu.click()) || noop()"
        />

        <SidebarNavDropdown
          v-if="menu.children && menu.children.length && (!menu.condition || menu.condition())"
          :key="$idx"
          :menu-text="menu.menuText"
          :menu-icon="menu.menuIcon"
          :children="menu.children"
          :route="menu.route.name"
        />
      </template>
    </b-nav>
  </b-sidebar>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import logoutMixin from '@/mixins/logout.mixin'
import SidebarNavDropdown from './SidebarNavDropdown.vue'
import SidebarNavItem from './SidebarNavItem.vue'
import { isRingbaIntegrationEnabled } from '@/services/featureFlags'
import { ACCOUNT_PERMISSIONS } from '@/store/account-permissions'
import { isAccountMenuAllowed } from '@/router/account-access'
import { DRAWER_LAYOUT_QUERY } from '@/utils/layoutTokens'

export default {
  name: 'TheSidebar',

  components: {
    SidebarNavDropdown,
    SidebarNavItem
  },

  mixins: [logoutMixin],

  data() {
    return {
      // b-sidebar's own shown state, which only the drawer uses: the column is shown by the SCSS.
      isDrawerOpen: false,

      menuOptions: [
        {
          menuText: 'Impersonate',
          menuIcon: ['fas', 'user-circle'],
          route: {
            name: 'AdminSwitchAccount'
          },
          condition: () => this.isSuperAdmin
        },
        {
          menuText: 'Dashboard',
          menuIcon: ['fas', 'tachometer-alt'],
          route: {
            name: 'SellerDashboard'
          },
          condition: () => false, // this.isSellerAccount,
          exact: true,
          badge: 'NEW'
        },
        {
          menuText: 'Call Logs',
          menuIcon: ['fas', 'phone-volume'],
          route: {
            name: 'SellerCallLogs'
          },
          condition: () => this.isSellerAccount
        },
        {
          menuText: 'Call Logs',
          menuIcon: ['fas', 'phone-volume'],
          route: {
            name: 'BuyerCallLogs'
          },
          condition: () => this.isBuyerAccount
        },
        {
          menuText: 'Campaigns',
          menuIcon: ['fas', 'chart-line'],
          condition: () => this.isBuyerAccount,
          route: {
            name: 'ListBuyerCampaigns|BuyerCreateCampaign|BuyerCreateSoftphoneCampaign'
          },
          children: [
            {
              subMenuText: 'Campaigns',
              route: {
                name: 'ListBuyerCampaigns'
              },
            },
            // The two Create items swap rather than stack. A softphone agent gets the softphone
            // flow; everyone else - staff impersonating included, even staff who are agents
            // themselves - gets the standard one, because a softphone campaign has to be created
            // by the agent it will ring. See `canCreateSoftphoneCampaign`.
            {
              subMenuText: 'Create Campaign',
              route: {
                name: 'BuyerCreateCampaign'
              },
              condition: () => !this.canCreateSoftphoneCampaign
            },
            {
              subMenuText: 'Create Campaign',
              route: {
                name: 'BuyerCreateSoftphoneCampaign'
              },
              condition: () => this.canCreateSoftphoneCampaign
            }
          ]
        },
        {
          menuText: 'Offers',
          menuIcon: ['fas', 'chart-line'],
          route: {
            name: 'SellerAvailableOffers'
          },
          condition: () => this.isSellerAccount
        },
        {
          menuText: 'Creatives',
          menuIcon: ['fas', 'photo-video'],
          route: {
            name: 'SellerCreatives'
          },          
          condition: () => this.isSellerAccount
        },
        {
          menuText: 'Creatives',
          menuIcon: ['fas', 'photo-video'],
          route: {
            name: 'ReviewCreatives'
          },          
          condition: () => this.isBuyerAccount && this.hasAllowedMedicareCategory
        },

        {
          menuText: 'Users',
          menuIcon: ['fas', 'users'],
          route: {
            name: 'UserManagement'
          },
          // User management is restricted to account admins, as is its route.
          condition: () => this.canManageUsers
        },

        {
          menuText: 'Settings',
          menuIcon: ['fas', 'cog'],
          route: {
            name: 'BuyerAccountBudget|BuyerCampaignSettings|BlockedChannels|AccountTimezone'
          },
          children: [
            {
              subMenuText: 'Account Budget',
              route: {
                name: 'BuyerAccountBudget'
              },
              condition: () => this.isBuyerAccount && this.accountDetails?.budget?.dailyBudgetInUSD
            },

            {
              subMenuText: 'Campaigns',
              route: {
                name: 'BuyerCampaignSettings'
              },
              condition: () => this.isBuyerAccount
            },

            {
              subMenuText: 'Blocked Channels',
              route: {
                name: 'BlockedChannels'
              },
              condition: () => this.isBuyerAccount
            },

            {
              subMenuText: 'Timezone',
              route: {
                name: 'AccountTimezone'
              }
            },
          ]
        },

        {
          menuText: 'Security',
          menuIcon: ['fas', 'key'],
          route: {
            name: 'TwoFactorAuth|ListApiTokens|CreateApiToken'
          },
          children: [
            {
              subMenuText: 'Multi-Factor Auth',
              route: {
                name: 'TwoFactorAuth'
              },
              'data-cy': 'button-2fa'
            },
            {
              subMenuText: 'API Tokens',
              route: {
                name: 'ListApiTokens'
              },
              'data-cy': 'button-api-tokens',
              condition: () => this.canManageApiKeys
            },
          ]
        },

        {
          menuText: 'Billing',
          menuIcon: ['fas', 'credit-card'],
          route: {
            name: 'BillingSettings|BankInfo|AddFunds|AddCreditCard|PaymentMethods|MyAccount'
          },
          // Every member sees balance and invoices; everything else needs the billing permission.
          children: [
            {
              subMenuText: 'My Account',
              route: {
                name: 'MyAccount'
              },
              'data-cy': 'button-my-account',
              badge: 'NEW'
            },
            {
              subMenuText: 'Payment Methods',
              route: {
                name: 'PaymentMethods'
              },
              'data-cy': 'button-payment-methods',
              condition: () => this.canViewBilling && this.isBuyerAccount,
            },
            {
              subMenuText: 'Add Credit Card',
              route: {
                name: 'AddCreditCard'
              },
              'data-cy': 'button-add-credit-card',
              condition: () => this.canViewBilling && this.isBuyerAccount,
            },
            {
              subMenuText: 'Add Funds',
              route: {
                name: 'AddFunds'
              },
              'data-cy': 'button-add-funds',
              condition: () => this.canViewBilling && this.isBuyerAccount,
            },
            {
              subMenuText: 'Advance Pay',
              route: {
                name: 'AdvancePay'
              },
              'data-cy': 'button-add-funds',
              condition: () => this.canViewBilling && this.isBuyerAccount,
            },
            {
              subMenuText: 'Bank Info',
              route: {
                name: 'BankInfo'
              },
              'data-cy': 'button-bank-info',
              condition: () => this.canViewBilling && this.isSellerAccount,
            },
            {
              subMenuText: 'Settings',
              route: {
                name: 'BillingSettings'
              },
              'data-cy': 'button-billing-settings',
              condition: () => this.canViewBilling && this.isBuyerAccount,
            },
          ]
        },
        {
          menuText: 'Help & Resources',
          menuIcon: ['fas', 'book'],
          route: {
            name: 'AdditionalResources'
          },
          children: [
            {
              subMenuText: 'Support Portal',
              href: 'https://support.ringbax.com',
              target: '_blank'
            }
          ]
        },
        {
          menuText: 'Ringba Integration',
          menuIcon: ['fas', 'link'],
          route: {
            name: 'RingbaIntegration'
          },
          condition: () => this.isSellerAccount && isRingbaIntegrationEnabled()
        },
        {
          menuText: 'Logout',
          menuIcon: ['fas', 'power-off'],
          href: 'javascript:;',
          route: {
            name: 'Logout'
          },
          click: this.logoutAndRedirect
        }
      ]
    }
  },  

  computed: {
    visibleMenuOptions() {
      const allowed = item => (!item.condition || item.condition()) &&
        isAccountMenuAllowed(item.route?.name, this.hasPermission)
      return this.menuOptions.filter(allowed).map(menu => ({
        ...menu,
        children: menu.children?.filter(allowed)
      })).filter(menu => !menu.children || menu.children.length)
    },
    ...mapState(['isSidebarExpanded']),    

    ...mapGetters('auth', [
      'isBuyerAccount',
      'isSellerAccount',
      'accountDetails',
      'hasAllowedMedicareCategory',
      'hasPermission',
      'isSuperAdmin'
    ]),

    ...mapGetters('globalSettings', ['canCreateSoftphoneCampaign']),

    canViewBilling() {
      return this.hasPermission(ACCOUNT_PERMISSIONS.BILLING)
    },

    canManageApiKeys() {
      return this.hasPermission(ACCOUNT_PERMISSIONS.ADMIN)
    },

    canManageUsers() {
      return this.hasPermission(ACCOUNT_PERMISSIONS.ADMIN)
    },

    sidebarClass() {
      let styleClasses = 'sidebar--column'
      return this.isSidebarExpanded
        ? styleClasses
        : `${styleClasses} sidebar--collapsed`
    }
  },  

  // The softphone menu item is gated on the global SOFTPHONE_GOES_LIVE setting, so it has to be
  // read before the menu renders. Cached in the store for 15 minutes.
  created() {
    this.getSoftphoneGoesLiveSetting()
  },

  // A drawer opened on a narrow or short window would otherwise stay open after the window grows
  // into the column, where the header's button only collapses the column. While it is open, the
  // tab stops b-sidebar puts on each side of it send the Tab key back into the sidebar, so a
  // keyboard could not reach the page.
  mounted() {
    this.drawerLayoutQuery = window.matchMedia?.(DRAWER_LAYOUT_QUERY)
    this.drawerLayoutQuery?.addEventListener?.('change', this.onDrawerLayoutChange)
  },

  beforeDestroy() {
    this.drawerLayoutQuery?.removeEventListener?.('change', this.onDrawerLayoutChange)
  },

  methods: {
    ...mapActions('globalSettings', ['getSoftphoneGoesLiveSetting']),

    onDrawerLayoutChange({ matches }) {
      if (!matches) {
        this.isDrawerOpen = false
      }
    },

    noop() {}
  }
}
</script>
